import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { UserCircle2, ChevronDown, ChevronUp, MessageSquare, Lock, Sparkles, BookOpen, GraduationCap, ShieldCheck, Award, HeartHandshake } from "lucide-react";

const COPY = {
  en: {
    eyebrow: "Community Court Navigator",
    heading: "Meet Nina Watts, Your Community Court Navigator",
    tagline: "Founder · Self-represented litigant · Author of Court Ready and Beyond the Courthouse Doors",
    subtitle: "Need help finding your way around DIYLegal.info?",
    intro: "The Community Court Navigator is a human support resource designed to help you navigate the website, understand available educational resources, locate tools, and learn how to use the information provided on the platform. Nina is not an attorney and does not provide legal advice.",
    credentials_toggle: "Credentials & Qualifications",
    credentials_groups: [
      {
        heading: "Foundations",
        icon: "foundations",
        items: [
          "Founder of DIYLegal.info — built the platform after her own journey through the civil-court system",
          "Self-represented litigant — hundreds of hours of first-hand courtroom experience",
          "Community advocate with a focus on procedural fairness and legal-literacy for underserved communities",
        ],
      },
      {
        heading: "Education",
        icon: "education",
        items: [
          "Master of Arts, Criminal Justice — 2026 · Governors State University",
          "Bachelor of Arts, Criminal Justice — Cum Laude · Governors State University, 2016",
          "CLEO Pre-Law Program (Legal Analysis & Policy Foundations) — Southern University Law Center",
        ],
      },
      {
        heading: "Experience",
        icon: "experience",
        items: [
          "Over 20+ years of experience across corrections, child welfare, program development, training, and community leadership",
        ],
      },
    ],
    cta_title: "Text a Community Court Navigator",
    cta_sub: "Questions about the site? Need help finding a resource? Text us. We're here to help you navigate the information — not tell you what legal strategy to use.",
    cta_button: "Text Nina",
    disclaimer_title: "IMPORTANT DISCLAIMER",
    disclaimer_body: [
      "The Community Court Navigator provides general educational and informational assistance only.",
      "DIYLegal.info and its Community Court Navigator do NOT provide legal advice, legal strategies, legal defenses, legal opinions, legal representation, or legal services of any kind.",
      "The Community Court Navigator is not acting as the user's attorney and does not establish an attorney-client relationship.",
      "Information provided through the Community Court Navigator is not a recommendation about what a user should do in a specific legal matter.",
      "Users who need legal advice, legal strategy, legal defenses, or legal representation should consult a licensed attorney or qualified legal-service provider.",
      "Do not submit confidential, privileged, financial-account, Social Security, medical, or other sensitive information through the navigator service.",
    ],
    session_title: "Live 30-minute one-on-one session",
    session_body: "A 30-minute one-on-one educational session with a Community Court Navigator to help you navigate DIYLegal.info and locate available educational and community resources.",
    session_premium_only: "Premium only",
    session_cta_premium: "Book my session",
    session_cta_upgrade: "Upgrade to Premium to book",
    session_signin: "Sign in and upgrade to Premium to book",
  },
  es: {
    eyebrow: "Navegadora de la Corte Comunitaria",
    heading: "Conoce a Nina Watts, tu Navegadora de la Corte Comunitaria",
    tagline: "Fundadora · Litigante auto-representada · Autora de Court Ready y Beyond the Courthouse Doors",
    subtitle: "¿Necesitas ayuda para orientarte en DIYLegal.info?",
    intro: "La Navegadora de la Corte Comunitaria es un recurso humano que te ayuda a usar el sitio, entender los recursos educativos disponibles, encontrar herramientas y aprender a usar la información que ofrece la plataforma. Nina no es abogada y no brinda asesoría legal.",
    credentials_toggle: "Credenciales y calificaciones",
    credentials_groups: [
      {
        heading: "Fundamentos",
        icon: "foundations",
        items: [
          "Fundadora de DIYLegal.info — creó la plataforma después de su propio proceso en la corte civil",
          "Litigante auto-representada — cientos de horas de experiencia directa en la sala",
          "Defensora comunitaria enfocada en el debido proceso y la alfabetización legal para comunidades desatendidas",
        ],
      },
      {
        heading: "Educación",
        icon: "education",
        items: [
          "Maestría en Justicia Penal — 2026 · Governors State University",
          "Licenciatura en Justicia Penal — Cum Laude · Governors State University, 2016",
          "Programa Pre-Jurídico CLEO (Análisis Legal y Fundamentos de Política) — Southern University Law Center",
        ],
      },
      {
        heading: "Experiencia",
        icon: "experience",
        items: [
          "Más de 20 años de experiencia en correcciones, bienestar infantil, desarrollo de programas, capacitación y liderazgo comunitario",
        ],
      },
    ],
    cta_title: "Envíale un mensaje a una Navegadora Comunitaria",
    cta_sub: "¿Preguntas sobre el sitio? ¿Necesitas ayuda para encontrar un recurso? Escríbenos. Estamos aquí para ayudarte a navegar la información — no para decirte qué estrategia legal usar.",
    cta_button: "Enviar mensaje a Nina",
    disclaimer_title: "AVISO IMPORTANTE",
    disclaimer_body: [
      "La Navegadora de la Corte Comunitaria brinda únicamente asistencia general educativa e informativa.",
      "DIYLegal.info y su Navegadora NO brindan asesoría legal, estrategias legales, defensas legales, opiniones legales, representación legal ni servicios legales de ningún tipo.",
      "La Navegadora no actúa como abogada del usuario y no se establece una relación abogado-cliente.",
      "La información proporcionada no es una recomendación sobre qué hacer en un asunto legal específico.",
      "Quienes necesiten asesoría, estrategia, defensa o representación legal deben consultar con un abogado con licencia o un proveedor calificado de servicios legales.",
      "No envíes información confidencial, privilegiada, de cuentas financieras, Seguro Social, médica u otra información sensible a través del servicio de la Navegadora.",
    ],
    session_title: "Sesión individual de 30 minutos en vivo",
    session_body: "Una sesión educativa individual de 30 minutos con la Navegadora de la Corte Comunitaria para ayudarte a usar DIYLegal.info y encontrar recursos educativos y comunitarios disponibles.",
    session_premium_only: "Solo Premium",
    session_cta_premium: "Reservar mi sesión",
    session_cta_upgrade: "Actualiza a Premium para reservar",
    session_signin: "Inicia sesión y actualiza a Premium para reservar",
  },
};

const ICONS = {
  foundations: BookOpen,
  education: GraduationCap,
  author: BookOpen,
  experience: ShieldCheck,
  training: Award,
  service: HeartHandshake,
};

export default function Navigator() {
  const { lang } = useLang();
  const { user } = useAuth();
  const t = COPY[lang] || COPY.en;
  const [open, setOpen] = useState(false);
  const signedIn = user && typeof user === "object";
  const isPremium = signedIn && user.is_premium;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold" data-testid="navigator-eyebrow">{t.eyebrow}</div>
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="navigator-heading">
          <div className="w-12 h-12 rounded-full bg-[#FAF5EC] border-2 border-[#E4DDD3] flex items-center justify-center text-[#C85A32]">
            <UserCircle2 className="w-7 h-7" />
          </div>
          {t.heading}
        </h1>
        <p className="mt-2 text-sm text-[#4A525A] pl-16" data-testid="navigator-tagline">{t.tagline}</p>
      </div>

      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
        <div className="text-lg font-bold text-[#1C2024]">{t.subtitle}</div>
        <p className="mt-2 text-[#1C2024] leading-relaxed">{t.intro}</p>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="navigator-credentials-toggle"
          className="pod-focus mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#C85A32]"
        >
          {t.credentials_toggle}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {open && (
          <div className="mt-4 space-y-5" data-testid="navigator-credentials">
            {t.credentials_groups.map((group, gi) => {
              const Icon = ICONS[group.icon] || Sparkles;
              return (
                <div key={gi} data-testid={`credentials-group-${group.icon}`}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C85A32] font-extrabold">
                    <Icon className="w-4 h-4" />
                    {group.heading}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {group.items.map((c, i) => {
                      const isLink = c && typeof c === "object" && c.href;
                      if (isLink && c.cover) {
                        return (
                          <li key={i} className="flex gap-3">
                            <BookCover cover={c.cover} />
                            <div className="flex-1 min-w-0">
                              <a
                                href={c.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`author-book-link-${i}`}
                                className="pod-focus font-semibold text-[#C85A32] hover:text-[#B24C28] underline underline-offset-2 decoration-2 decoration-[#C85A32]/40 hover:decoration-[#C85A32] text-sm"
                              >
                                {c.text}
                              </a>
                              <div className="text-[11px] font-bold uppercase tracking-widest text-[#707880] mt-1">
                                {lang === "es" ? "Comprar en Amazon" : "Buy on Amazon"} →
                              </div>
                            </div>
                          </li>
                        );
                      }
                      return (
                        <li key={i} className="flex gap-3 text-sm text-[#1C2024]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#C85A32] mt-2 shrink-0" />
                          {isLink ? (
                            <a
                              href={c.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`author-book-link-${i}`}
                              className="pod-focus font-semibold text-[#C85A32] hover:text-[#B24C28] underline underline-offset-2 decoration-2 decoration-[#C85A32]/40 hover:decoration-[#C85A32]"
                            >
                              {c.text}
                            </a>
                          ) : (
                            <span>{c}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-[#FAF5EC] border-2 border-[#E4DDD3] rounded-2xl p-5">
        <div className="text-lg font-bold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#C85A32]" /> {t.cta_title}</div>
        <p className="text-[#1C2024] mt-2">{t.cta_sub}</p>
        <a
          href="sms:+13312510466"
          data-testid="navigator-text-cta"
          className="pod-focus mt-4 inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-5 rounded-xl"
        >
          <MessageSquare className="w-4 h-4" /> {t.cta_button}
        </a>
      </div>

      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5" data-testid="navigator-session">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-extrabold uppercase tracking-widest text-[#1C2024]">{t.session_title}</div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest bg-[#C85A32] text-white px-2.5 py-1 rounded-full" data-testid="session-premium-badge">
            <Lock className="w-3 h-3" /> {t.session_premium_only}
          </span>
        </div>
        <p className="mt-2 text-[#1C2024]">{t.session_body}</p>
        <div className="mt-4">
          {isPremium ? (
            <a
              href="mailto:hello@diylegal.info?subject=Book%2030-minute%20session"
              data-testid="session-book-cta"
              className="pod-focus inline-flex items-center gap-2 bg-[#2C4A3E] hover:bg-[#1F382E] text-white font-bold py-3 px-5 rounded-xl"
            >
              <Sparkles className="w-4 h-4" /> {t.session_cta_premium}
            </a>
          ) : signedIn ? (
            <Link
              to="/summarize"
              data-testid="session-upgrade-cta"
              className="pod-focus inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-5 rounded-xl"
            >
              <Lock className="w-4 h-4" /> {t.session_cta_upgrade}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="session-signin-cta"
              className="pod-focus inline-flex items-center gap-2 bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-3 px-5 rounded-xl"
            >
              <Lock className="w-4 h-4" /> {t.session_signin}
            </Link>
          )}
        </div>
      </div>

      <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5" data-testid="navigator-disclaimer">
        <div className="text-sm font-extrabold text-amber-900">{t.disclaimer_title}</div>
        <ul className="mt-2 space-y-2 text-sm text-amber-900">
          {t.disclaimer_body.map((line, i) => (<li key={i}>{line}</li>))}
        </ul>
      </div>
    </div>
  );
}

function BookCover({ cover }) {
  const schemes = {
    terracotta: {
      bg: "linear-gradient(155deg,#C85A32 0%,#8B3A1E 62%,#4A1D0A 100%)",
      accent: "#F5E6D3",
      shadow: "rgba(74,29,10,.45)",
    },
    forest: {
      bg: "linear-gradient(155deg,#2C4A3E 0%,#1B3128 62%,#0D1A13 100%)",
      accent: "#E8D9A8",
      shadow: "rgba(13,26,19,.45)",
    },
  };
  const s = schemes[cover.scheme] || schemes.terracotta;
  return (
    <div
      className="shrink-0"
      data-testid="book-cover"
      aria-hidden="true"
    >
      <div
        className="w-16 sm:w-20 aspect-[2/3] rounded-md relative overflow-hidden"
        style={{ background: s.bg, boxShadow: `0 8px 18px -6px ${s.shadow}, inset 2px 0 0 rgba(255,255,255,.08), inset -2px 0 0 rgba(0,0,0,.25)` }}
      >
        {/* spine highlight */}
        <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: "linear-gradient(180deg,rgba(255,255,255,.25),rgba(255,255,255,0) 70%)" }} />
        {/* frame */}
        <div className="absolute inset-1.5 border rounded-[3px]" style={{ borderColor: `${s.accent}55` }} />
        <div className="absolute inset-0 flex flex-col justify-between p-1.5 sm:p-2 text-center">
          <div>
            <div className="text-[9px] sm:text-[10px] font-extrabold tracking-[0.15em] leading-tight" style={{ color: s.accent }}>
              {cover.title}
            </div>
            {cover.titleLine2 && (
              <div className="text-[7px] sm:text-[8px] font-bold tracking-wider leading-tight mt-0.5" style={{ color: s.accent }}>
                {cover.titleLine2}
              </div>
            )}
          </div>
          <div className="text-[5px] sm:text-[6px] font-semibold uppercase tracking-widest leading-tight" style={{ color: `${s.accent}CC` }}>
            {cover.subtitle}
          </div>
          <div className="text-[6px] sm:text-[7px] font-extrabold tracking-[0.15em]" style={{ color: s.accent }}>
            {cover.author}
          </div>
        </div>
      </div>
    </div>
  );
}

